import { Trans, useLingui } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import { formatBrand } from "@mobility-item-page/utilities";
import type { WarpModal } from "@warp-ds/elements/modal";
import type { WarpRadio } from "@warp-ds/elements/radio";
import { Alert } from "@warp-ds/elements/react/alert";
import { Button } from "@warp-ds/elements/react/button";
import { Modal, ModalFooter, ModalHeader } from "@warp-ds/elements/react/modal";
import { Radio } from "@warp-ds/elements/react/radio";
import { RadioGroup } from "@warp-ds/elements/react/radio-group";
import { TextField } from "@warp-ds/elements/react/textfield";
import { type Ref, type SubmitEvent, useId, useImperativeHandle, useRef, useState } from "react";
import { ObjectPosition } from "../../../types/objectPosition.ts";
import { useValidDealerItemContext } from "../../context/DealerItemProviderContext.tsx";
import {
  CALLBACK_TIME_SLOTS,
  type CallbackTimeSlot,
  callbackMessage,
  callbackTimeSlotLabel,
  DEFAULT_CALLBACK_TIME_SLOT,
  isCallbackTimeSlot,
} from "../../utils/callbackTimeSlots.ts";
import { useTracking } from "../../utils/pulseHelper.ts";
import { usePrefilledPhoneNumber } from "../../utils/usePrefilledPhoneNumber.ts";
import { validatePhone } from "../../utils/validatePhone.ts";
import { ProfileEmail } from "./ProfileEmail.tsx";

enum CallMeBackFormState {
  Initial,
  Submitting,
  Success,
  InvalidEid,
  InvalidLogin,
  Error,
}

export type CallMeBackModalHandle = {
  open: () => void;
};

export const CallMeBackModal = ({ ref }: { ref?: Ref<CallMeBackModalHandle> }) => {
  const modalRef = useRef<WarpModal>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [formState, setFormState] = useState<CallMeBackFormState>(CallMeBackFormState.Initial);
  const {
    organisationData: { name },
    itemData: { adId },
    external: { isMobile, brand },
  } = useValidDealerItemContext();
  const prefilledPhoneNumber = usePrefilledPhoneNumber();
  const [phoneNumber, setPhoneNumber] = useState(prefilledPhoneNumber);
  const [timeSlot, setTimeSlot] = useState<CallbackTimeSlot>(DEFAULT_CALLBACK_TIME_SLOT);
  const [phoneError, setPhoneError] = useState("");
  const [errorCode, setErrorCode] = useState(0);
  const formId = useId();
  const { i18n } = useLingui();
  const { trackCancelContactFormModal, trackSubmitContactForm } = useTracking();

  // onHidden fires from the web component, so read the state through a ref rather than a stale closure
  const formStateRef = useRef(formState);
  formStateRef.current = formState;

  useImperativeHandle(ref, () => ({
    open: () => {
      modalRef.current?.open();
    },
  }));

  const onHidden = () => {
    if (formStateRef.current === CallMeBackFormState.Success) {
      // Kept until now because the success text shows the picked slot
      setTimeSlot(DEFAULT_CALLBACK_TIME_SLOT);
    } else {
      trackCancelContactFormModal(ObjectPosition.CallMeBack);
    }
    setPhoneError("");
    setFormState(CallMeBackFormState.Initial);
  };

  const title = i18n.t({
    id: "dealer.callMeBack.title",
    message: "Be called by {dealerName}",
    values: { dealerName: name },
    comment: "Header for the call me back form",
  });

  const errorText = i18n.t({
    id: "dealer.contactForm.errorText.generic",
    message: "An error occurred while sending your message.\n" + "Please try again later. (Error code: {errorCode})",
    values: { errorCode },
    comment:
      "Generic error text displayed when an unexpected error occurs while submitting the contact form, includes error code",
  });

  // Unlike the contact form the phone number is the only way the dealer can reach back, so it is mandatory here.
  const validatePhoneNo = (): boolean => {
    if (phoneNumber.trim().length === 0) {
      setPhoneError(
        i18n.t({
          id: "dealer.contactForm.phone.helpText.required",
          message: "Enter phone number",
          comment: "Help text when the phone number field is empty, displayed underneath the phone number field",
        }),
      );
      return false;
    }
    if (!validatePhone(phoneNumber, brand)) {
      setPhoneError(
        i18n.t({
          id: "dealer.contactForm.phone.helpText.invalid",
          message: "Enter a valid phone number",
          comment: "Help text when the phone number format is invalid, displayed underneath the phone number field",
        }),
      );
      return false;
    }
    setPhoneError("");
    return true;
  };

  const handlePhoneChange = (newPhone: string) => {
    setFormState(CallMeBackFormState.Initial);
    setPhoneError("");
    setPhoneNumber(newPhone.trim().replace(/\s/g, ""));
  };

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();

    if (!validatePhoneNo()) {
      return;
    }
    setFormState(CallMeBackFormState.Submitting);

    try {
      const response = await fetch(`/mobility/api/contact/${adId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: callbackMessage(i18n, timeSlot),
          phoneNumber,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 401 && data.message === "Not logged in") {
          setFormState(CallMeBackFormState.InvalidLogin);
          return;
        }
        if (response.status === 401 && data.message === "eID verification failed") {
          setFormState(CallMeBackFormState.InvalidEid);
          return;
        }
        setErrorCode(response.status);
        throw new Error(`Failed to send message: ${response.status} ${response.statusText} ${data.message}`);
      }

      trackSubmitContactForm(phoneNumber, { name: "Call Me Back", position: ObjectPosition.CallMeBack });
      setPhoneNumber(prefilledPhoneNumber);
      setFormState(CallMeBackFormState.Success);
    } catch (error) {
      console.error("Error sending call me back form:", error);
      setFormState(CallMeBackFormState.Error);
    }
  };

  // The group dispatches change at group level with no target element, so read the selection off the group itself.
  const handleTimeSlotChange = (event: Event) => {
    const selected = (event.target as HTMLElement).querySelector<WarpRadio>("w-radio[checked]");
    if (isCallbackTimeSlot(selected?.value)) {
      setFormState(CallMeBackFormState.Initial);
      setTimeSlot(selected.value);
    }
  };

  return (
    <Modal ref={modalRef} onHidden={onHidden}>
      <ModalHeader slot="header" title={title} />
      <div slot="content">
        {formState !== CallMeBackFormState.Success ? (
          <form id={formId} ref={formRef} onSubmit={handleSubmit} noValidate>
            <p className="mb-16">
              <Trans
                id="dealer.callMeBack.introText"
                message="Leave your number and choose a time that suits you. The dealer may call you outside your preferred time."
                comment="Intro text at the top of the call me back form, above the phone number field"
              />
            </p>
            <TextField
              label={i18n.t({
                id: "dealer.contactForm.phone.label",
                message: "Phone number",
                comment: "Label for the phone number text field",
              })}
              placeholder={i18n.t({
                id: "dealer.contactForm.phone.placeholder",
                message: "Enter your phone number",
                comment:
                  "Placeholder for the phone number field, using the country code for each country. Only the default (english) is a text",
              })}
              type="tel"
              data-testid="call-me-back-phone"
              disabled={formState === CallMeBackFormState.Submitting}
              invalid={phoneError !== ""}
              helpText={phoneError}
              aria-errormessage={phoneError !== "" ? phoneError : undefined}
              value={phoneNumber}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={() => validatePhoneNo()}
              className="block mb-16"
            />
            <RadioGroup
              name="callbackTimeSlot"
              label={i18n.t({
                id: "dealer.callMeBack.timeSlot.title",
                message: "When would you like to be called?",
                comment: "Title above the options for when the user wants the dealer to call back",
              })}
              disabled={formState === CallMeBackFormState.Submitting}
              onChange={handleTimeSlotChange}
              className="block mb-24"
            >
              {CALLBACK_TIME_SLOTS.map((slot) => (
                <Radio key={slot} value={slot} checked={slot === timeSlot} data-testid={`call-me-back-${slot}`}>
                  {callbackTimeSlotLabel(i18n, slot)}
                </Radio>
              ))}
            </RadioGroup>
            <div className="text-xs s-text-subtle">
              <Trans
                id="dealer.contactForm.infoText.dynamic"
                message="When you send this message, your name{hasPhoneNumber, select, yes {, e-mail and phone number} other { and e-mail}} are transferred to the seller, who will then be responsible for your data. The message is sent via your registered email. {brand} may review and block messages to prevent fraud."
                values={{
                  brand: formatBrand(brand),
                  hasPhoneNumber: "yes",
                }}
              />
              <ProfileEmail />
            </div>
            <Alert show={formState === CallMeBackFormState.InvalidLogin} variant="negative" className="mb-24">
              <h2 className="t4">
                <Trans
                  id="dealer.contactForm.loginErrorText.title"
                  message="You have been logged out"
                  comment="Alert title shown on invalid login error"
                />
              </h2>
              <p>
                <Trans
                  id="dealer.contactForm.loginErrorText.description"
                  message="It looks like your session has expired. Log in again to send your message to the seller."
                  comment="Alert description displayed when a user was unauthorised while submitting the contact form"
                />
              </p>
              <Button variant="secondary" href="/auth/login" target="_blank">
                <span className="flex items-center gap-8">
                  <Trans id="dealer.contactForm.loginErrorButton.text" message="Log in (opens in new window)" />
                  <WIcon name="LinkExternal" />
                </span>
              </Button>
            </Alert>
            <Alert show={formState === CallMeBackFormState.InvalidEid} variant="negative" className="mb-24">
              <h2 className="t4">
                <Trans
                  id="dealer.eidModal.title"
                  message="Verify your identity"
                  comment="Alert title shown when eID verification is required or has expired"
                />
              </h2>
              <p>
                <Trans
                  id="dealer.eidModal.description.sendMessage"
                  message="Before contacting someone, you need to verify your account. This lets the person on the other end feel confident about who they are talking to."
                  comment="Alert description displayed when eID verification is required to submit the contact form"
                />
              </p>
              <Button href="/auth/login/eid" target="_blank">
                <span className="flex items-center gap-8">
                  <Trans
                    id="dealer.eidModal.verifyButton"
                    message="Verify with Electronic ID"
                    comment="Text for button to go to the BankID / MitId validation page. The text should contain the correct validator for each country (eg. bankId for NB and SV, and MitID for DA)"
                  />
                  <WIcon name="LinkExternal" />
                </span>
              </Button>
            </Alert>
            <Alert show={formState === CallMeBackFormState.Error} variant="negative" className="mb-24">
              {errorText}
            </Alert>
          </form>
        ) : (
          <div className="flex flex-col gap-24">
            <Trans
              id="dealer.callMeBack.success.text"
              message="We have informed the dealer that you wish to be contacted {timeSlot, select, morning {between 08:00 and 12:00} afternoon {between 12:00 and 16:00} evening {after 16:00} other {as soon as possible}}."
              values={{ timeSlot }}
              comment="Message shown when the call me back form is sent successfully. 'timeSlot' is the time the user picked; the 'other' branch is the default 'as soon as possible' option"
            />
          </div>
        )}
      </div>
      <ModalFooter slot="footer">
        {formState !== CallMeBackFormState.Success ? (
          <Button
            variant="primary"
            type="submit"
            data-testid="call-me-back-submit"
            onClick={() => formRef.current?.requestSubmit()}
            loading={formState === CallMeBackFormState.Submitting}
            fullWidth={isMobile}
          >
            <Trans id="dealer.callMeBack.submitButton.text" message="Ask to be called" />
          </Button>
        ) : (
          <Button
            variant="secondary"
            fullWidth={isMobile}
            className="self-end"
            onClick={() => {
              modalRef.current?.close();
            }}
          >
            <Trans id="dealer.modal.close.button" message="Close" />
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
